import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FacebookService, InitParams, LoginOptions, LoginResponse } from 'ngx-facebook';
import { of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { Page, PageSummary } from 'ideta-library/lib/common/channels/meta';
import { MetaUserInfos } from 'ideta-library/lib/common/user';

import { environment } from '../../../../environments/environment';

import { FeedbackService } from '../feedback/feedback.service';

@Injectable({
  providedIn: 'root'
})
export class MetaService {
  public localFacebook: Partial<MetaUserInfos>;

  constructor(
    private httpClient: HttpClient,
    private feedbackService: FeedbackService,
    private fb: FacebookService
  ) {
    fb.init(environment.facebook as InitParams);
  }

  async connectToFb(getAdsPosts?: boolean): Promise<Partial<MetaUserInfos>> {
    if (!this.localFacebook) {
      let scopes: string = [
        'public_profile',
        'pages_manage_metadata',
        'pages_show_list',
        'pages_messaging',
        'pages_read_user_content',
        'pages_manage_engagement',
        'pages_read_engagement',
        'business_management'
      ].join(',');
      if (getAdsPosts) scopes += ',ads_read';

      const options: LoginOptions = {
        scope: scopes,
        return_scopes: true,
        enable_profile_selector: true
      };

      this.localFacebook = await this.fb
        .login(options)
        .then(async (auth: LoginResponse) => {
          // Force local disconnection once the access token has expired
          setTimeout(() => this.disconnectFromFb(), auth.authResponse.expiresIn * 1000);
          return {
            accessToken: auth.authResponse.accessToken,
            id: auth.authResponse.userID,
            granted_scopes: (auth.authResponse.grantedScopes || '').split(',')
          };
        })
        .catch(() => null);
    }

    return this.localFacebook;
  }

  async disconnectFromFb(): Promise<void> {
    try {
      const status = await this.fb.getLoginStatus(false);
      if (status.authResponse) return this.fb.logout();
    } catch (err) {
      return Promise.resolve(err);
    } finally {
      this.localFacebook = null;
    }
  }

  connectToBusinessLogin(userId: string): void {
    const scope: string = ['business_basic', 'business_manage_comments', 'business_manage_messages'].join(',');
    let uri = `${environment.instagram.redirect_uri}`;
    // Note about the ∏ replacement : firebase userIds can end with _ which will make the parsing fail in the backend, so we have to escape them
    let authUri = `${environment.instagram.auth_uri}?client_id=${environment.instagram.appId}&redirect_uri=${uri}&response_type=code&scope=${scope}&state=${userId.replace(/_/g, '∏')}___aicm___none___autoclose`;

    window.open(authUri, '_blank');
  }

  async getPages(targetId?: string): Promise<Page[]> {
    return this.connectToFb()
      .then(creds =>
        !creds
          ? []
          : this.httpClient
              .get<any>(`${environment.facebook.url}/${creds.id}/accounts`, {
                params: { access_token: creds.accessToken, limit: 500 }
              })
              .pipe(
                map(res => res.data),
                take(1)
              )
              .toPromise()
      )
      .then((rawPages: PageSummary[]) =>
        Promise.all(
          rawPages
            .filter(({ id }: PageSummary) => !targetId || id === targetId)
            .map(({ id, access_token, name }: PageSummary) =>
              this.getPage(id, access_token).then((page: Page) => ({
                // order of properties is important here
                name,
                ...page,
                id,
                access_token
              }))
            )
        )
      )
      .catch((err: any) => {
        console.log('err : ', err);
        this.feedbackService.showMessage({
          type: 'message',
          decoration: 'error',
          message: 'There was an error, please contact support.',
          bypassMessageAutoTranslate: true
        });
        return [];
      });
  }

  private getPage(pageId: string, access_token: string): Promise<Page> {
    const requestedFields = ['name', 'about', 'picture{url}'];

    return this.httpClient
      .get<Page>(`${environment.facebook.url}/${pageId}`, {
        params: { access_token, fields: requestedFields.join(',') }
      })
      .pipe(
        catchError(_error => {
          console.log(_error);
          // ###M console.error(error);
          return of(null);
        }),
        take(1)
      )
      .toPromise();
  }
}
